/**
 * T486 — the glanceable lead strip on the QUOTING screens.
 *
 * `CustomerInfoBlock` puts everything the customer told us on the Quote screen
 * — the LAST screen. The screens Helen actually quotes THROUGH (Location, and
 * the golf variant inside it; Passengers; Add-ons) carried nothing, so she
 * picked a golf hub, a band and add-ons with the lead's own words off screen.
 * The golf entry was the worst of it: "Which golf run?" with three start
 * hotels and no sign of the course the customer had already named.
 *
 * Deliberately three quiet lines, not a second copy of the customer card:
 * Helen is mid-decision here, not reading a record (the simplicity rule). The
 * card lives on Quote and stays the full read.
 *
 * Renders ONLY for an inquiry-sourced job. A walk-in has no lead to show, and
 * that gate is also what keeps every walk-in e2e journey byte-identical.
 *
 * Read-only, text only: no buttons, no headings, no interactive elements. The
 * band renders here as TEXT beside the Passengers screen's band BUTTONS, and
 * the net matches those by role — a control here would collide with them.
 */
import type { JobState } from '../store/state.js';
import { contactPreferenceLabel } from '../../shared/send-schema.js';

export function LeadContext({ job, serviceLabel, summary }: {
  job: JobState;
  /** From the registry, never a literal (the copy gate bans service literals). */
  serviceLabel: string | null;
  /** The shared one-liner already shown on Quote and Done: name, route, when,
   *  band, extras. Reused verbatim so the two surfaces cannot drift. */
  summary: string;
}) {
  if (job.prefillSource !== 'inquiry') return null;

  // What they asked FOR. Course and venue matter most here: the course is the
  // literal answer to the golf screen's question, and neither appears in the
  // journey summary for an unresolved lead. T907 adds the collection place: on
  // golf it is the OTHER end of the priced pair, so this is the one surface that
  // shows Helen both ends while she is quoting.
  const asked = [serviceLabel, job.courseText, job.collectionPlace, job.venue].filter(Boolean).join(' · ');
  // How to reach them, and how they asked to be reached (T484's renderer, so a
  // retired stored value is worded rather than printed raw).
  const prefers = contactPreferenceLabel(job.contactPreference);
  const contact = [job.customerPhone, prefers && `Prefers ${prefers}`]
    .filter(Boolean)
    .join(' · ');

  if (!asked && !summary && !contact) return null;

  return (
    <div class="customer-info" role="group" aria-label="From the website">
      <p class="customer-info-title">From the website</p>
      {/* ⚠️ T1749 M8 (DS-21): `enquiry-meta`, not `inquiry-meta`. This strip wore a
          class the INBOX owned, so a change to Inbox's card moved three lines on
          a screen nobody was looking at; the rule is byte-identical under the
          house spelling and this strip now shares it with the card it is a
          summary of. `inbox.spec.tsx` and `lead-context.spec.tsx` grade the
          absence of the old prefix on both. */}
      {asked && <p class="enquiry-meta">{asked}</p>}
      {summary && <p class="enquiry-meta">{summary}</p>}
      {contact && <p class="enquiry-meta">{contact}</p>}
    </div>
  );
}
